import { formatDate } from '@/shared/domain/format';
import { fechaCaracas } from '@/shared/domain/freshness';
import type { OpcionConflicto } from '@/shared/domain/prefsConflicto';
import type { PreferenciasServidor } from '@/shared/domain/types';
import { usePrefs } from '@/shared/hooks/usePrefs';
import { useT } from '@/shared/i18n/useT';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { feedbackMsgs } from './feedbackMsgs';

export interface PrefsConflictViewProps {
  conflict: PreferenciasServidor | null;
  localFavoritos: number;
  onResolve: (opcion: OpcionConflicto) => void;
}

/** Diálogo de conflicto de preferencias (versión de la cuenta vs. este dispositivo). Obliga a elegir. */
export function PrefsConflictView({ conflict, localFavoritos, onResolve }: PrefsConflictViewProps) {
  const { t, lang } = useT(feedbackMsgs);
  const date = formatDate(fechaCaracas(conflict?.actualizado ?? ''), 'media', lang);
  const options: { id: OpcionConflicto; label: string; help: string }[] = [
    {
      id: 'servidor',
      label: t('conflict.server'),
      help: t('conflict.serverHelp', { date, count: conflict?.config.favoritos.length ?? 0 }),
    },
    {
      id: 'local',
      label: t('conflict.local'),
      help: t('conflict.localHelp', { count: localFavoritos }),
    },
    { id: 'mezclar', label: t('conflict.merge'), help: t('conflict.mergeHelp') },
  ];
  return (
    <Dialog
      open={conflict !== null}
      onOpenChange={() => undefined}
      title={t('conflict.title')}
      description={t('conflict.body')}
      role="alertdialog"
      hideClose
    >
      <ul className="flex flex-col gap-3">
        {options.map((o, i) => (
          <li key={o.id} className="flex flex-col gap-1">
            <Button
              variant={i === 0 ? 'primary' : 'secondary'}
              className="w-full"
              onClick={() => onResolve(o.id)}
            >
              {o.label}
            </Button>
            <p className="px-1 text-xs text-fg-muted">{o.help}</p>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/** Conectado al store: se monta una vez en `RootLayout`. */
export function PrefsConflictDialog() {
  const { conflict, config, resolveConflict } = usePrefs();
  return (
    <PrefsConflictView
      conflict={conflict}
      localFavoritos={config.favoritos.length}
      onResolve={resolveConflict}
    />
  );
}
